import { postRequest, getRequest } from './api';
import { STORAGE_KEYS } from '../config/constants';

export const authService = {
  /**
   * Step 1: Manager Signup - mobile + password
   * Sends OTP to the phone number
   */
  ManagerSignup: async (mobileNumber, password) => {
    try {
      const response = await postRequest('users/manager-signup', {
        mobileNumber,
        password,
      });
      return response?.data || response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Step 2: Verify OTP
   */
  verifyOtp: async (mobileNumber, code) => {
    try {
      const response = await postRequest('users/otp/verify', {
        mobileNumber,
        code: parseInt(code),
      });

      if (response?.data?.token) {
        localStorage.setItem(STORAGE_KEYS.TOKEN, response.data.token);
        localStorage.setItem(STORAGE_KEYS.USER_ID, response.data.userId);
        if (response.data.user) {
          localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.data.user));
        }
        // Also store the status
        if (response.data.status) {
          localStorage.setItem('userStatus', response.data.status);
        }
      }

      return response?.data || response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Resend OTP
   */
  resendOtp: async (mobileNumber) => {
    try {
      const response = await postRequest('users/manager-resend-otp', {
        mobileNumber,
      });
      return response || response?.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Step 3: Submit signature and policy agreement
   */
  submitAgreement: async (userId, signatureData, agreementData) => {
    try {
      const response = await postRequest('users/manager-agreement', {
        userId,
        signature: signatureData,
        ...agreementData,
      });
      return response || response?.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Step 4: Submit documents
   */
  submitDocuments: async (userId, documentData) => {
    try {
      const response = await postRequest('users/manager-submit-docs', {
        userId,
        ...documentData,
      });
      return response || response?.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Upload an image to S3
   */
  uploadImage: async (fileData, fileName, fileType) => {
    try {
      const response = await postRequest('images/upload', {
        fileName,
        fileData,
        fileType,
      });
      return response || response?.data;
    } catch (error) {
      throw error;
    }
  },

  // Login with mobile/email and password
  login: async (identifier, password) => {
    try {
      const response = await postRequest('users/manager-login', {
        mobileNumber: identifier,
        password,
      });

      if (response?.data?.token && response?.data?.userId) {
        localStorage.setItem(STORAGE_KEYS.TOKEN, response.data.token);
        localStorage.setItem(STORAGE_KEYS.USER_ID, response.data.userId);
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.data.user));
        if (response.data.user?.status) {
          localStorage.setItem('userStatus', response.data.user.status);
        }
      }

      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  // Register new manager (simplified version)
  register: async (userData) => {
    try {
      const response = await postRequest('users/manager-signup', userData);
      return response?.data || response;
    } catch (error) {
      throw error;
    }
  },

  // Logout
  logout: async () => {
    try {
      const userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
      if (userId) {
        await getRequest('users/logout');
      }
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_ID);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      localStorage.removeItem('userStatus');
    } catch (error) {
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_ID);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      localStorage.removeItem('userStatus');
      throw error;
    }
  },

  // Get current user data
  getCurrentUser: async () => {
    try {
      const userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
      if (!userId) return null;

      const response = await getRequest(`users/${userId}`);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response));
      if (response?.status) {
        localStorage.setItem('userStatus', response.status);
      }
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
    return !!(token && userId);
  },

  // Get stored user data
  getStoredUserData: () => {
    const userDataStr = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    return userDataStr ? JSON.parse(userDataStr) : null;
  },

  // Get user verification status
  getUserStatus: () => {
    return localStorage.getItem('userStatus') || 'unverified';
  },
};

export default authService;